import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { setupCropContainerScrollBlock } from 'utils/campaign/cropModal';
import {
    checkImagesInCropLoaded,
    checkRequiredImagesUploaded,
    getInputCropTypes,
    getLoadingCropType,
} from 'utils/clickme';
import { BANNER_IMAGE_CROP_TYPES, UpscaleStatus } from 'utils/constants/banner';
import { MagritteNotificationName } from 'utils/constants/notification';

import generateImageCropSaveFromSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_design/generate_image_crop_save_form_submit';
import cpcFlexiblePictureSaveButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/cpc_flexible_picture_save_button_click';
import { ActionBar, BottomSheet, Button, Modal, NavBar, Text } from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';
import useIsEditCampaign from 'utils/campaign/hooks/useIsEditCampaign';
import useIsNewCampaign from 'utils/campaign/hooks/useIsNewCampaign';

import { ImageContent } from 'components/common/BannerImg/CropImageModal/ImageContent/ImageContent';
import { UpscaleLoader } from 'components/common/BannerImg/CropImageModal/ImageContent/UpscaleLoader/UpscaleLoader';
import { useCropImageModal } from 'components/common/BannerImg/CropImageModal/useCropImageModal';
import { UpscaleCancelConfirmModal } from 'components/common/BannerImg/UpscaleCancelConfirmModal/UpscaleCancelConfirmModal';
import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';

import { selectMainMenuVacancyId } from 'store/models/mainMenu';
import { addMagritteNotification } from 'store/models/notification';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/common/BannerImg/CropImageModal/CropImageModal.module.less';

const TrlKeys = {
    title: 'AdCreate.AdForm.CropImage.title',
    titleBottomSheet: 'AdCreate.AdForm.CropImage.titleBottomSheet',
    description: 'AdCreate.AdForm.CropImage.description',
    buttons: {
        save: 'AdCreate.AdForm.CropImage.save',
        cancel: 'AdCreate.AdForm.CropImage.back',
    },
};

export const CropImageModal: FC<{ isQuickCampaign?: boolean }> = ({ isQuickCampaign }) => {
    const [isRequiredImagesErrorVisible, setIsRequiredImagesErrorVisible] = useState(false);
    const [
        isUpscaleCancelConfirmModalVisible,
        setIsUpscaleCancelConfirmModalVisibleOn,
        setIsUpscaleCancelConfirmModalVisibleOff,
    ] = useOnOffState(false);

    const vacancyId = useSelector(selectMainMenuVacancyId);
    const selectors = useSelector((state) => state.aiImageGeneration.selectors);
    const accountId = useSelector((state) => state.user?.user?.currentAccount.id);

    const trls = useTrls();
    const dispatch = useDispatch();

    const cropContainerRef = useRef<HTMLDivElement>(null);

    const isCampaignProcessEdit = useIsEditCampaign();
    const isCampaignProcessCreate = useIsNewCampaign();

    const {
        isCropImageModalVisible,
        images,
        isCropImageModalWasOpenedFromGeneration,
        resetModal,
        handleCloseModal,
        handleCropsSave,
    } = useCropImageModal(isQuickCampaign);

    const isAllImagesInCropLoaded = checkImagesInCropLoaded(images, BANNER_IMAGE_CROP_TYPES);
    const isAllRequiredImagesUploaded = checkRequiredImagesUploaded(images, BANNER_IMAGE_CROP_TYPES);
    const loadingCropType = getLoadingCropType(images);
    const inputCropType = getInputCropTypes(images);
    const isFullUpscaleLoading =
        Object.values(images).some((image) => image.upscaleStatus === UpscaleStatus.None) && !!inputCropType?.length;

    // Отслеживаем количество загруженных изображений для пересоздания обработчиков при подгрузке новых
    const loadedImagesCount = useMemo(
        () =>
            BANNER_IMAGE_CROP_TYPES.filter((cropType) => {
                const image = images[cropType];
                return image?.id && image?.isLoadedInCrop;
            }).length,
        [images]
    );

    const onCloseModal = () => {
        const isUpscaleLoading = loadingCropType && Object.values(images).some(({ upscaleStatus }) => !!upscaleStatus);
        if (isUpscaleLoading) {
            setIsUpscaleCancelConfirmModalVisibleOn();
        } else {
            handleCloseModal();
        }
    };

    const onCropsSave = () => {
        if (isCropImageModalWasOpenedFromGeneration) {
            selectors &&
                generateImageCropSaveFromSubmit({
                    accountId: accountId ?? 0,
                    imageId: images.MY_TARGET_DEFAULT.id ?? 0,
                    ...selectors,
                });
        }
        if (isCampaignProcessCreate) {
            cpcFlexiblePictureSaveButtonClick({ vacancyId });
        }
        if (isCampaignProcessEdit && !isAllRequiredImagesUploaded) {
            setIsRequiredImagesErrorVisible(true);
            return;
        }

        handleCropsSave();
    };

    useEffect(() => {
        if (!loadingCropType) {
            setIsUpscaleCancelConfirmModalVisibleOff();
        }
    }, [loadingCropType, setIsUpscaleCancelConfirmModalVisibleOff]);

    useEffect(() => {
        if (isRequiredImagesErrorVisible) {
            dispatch(addMagritteNotification(MagritteNotificationName.CropModalRequiredImagesError));
            setIsRequiredImagesErrorVisible(false);
        }
    }, [isRequiredImagesErrorVisible, dispatch]);

    useEffect(() => {
        const isOnlyUpscaleLoaderVisible = isFullUpscaleLoading && loadingCropType;
        if (!isCropImageModalVisible || !cropContainerRef.current || isOnlyUpscaleLoaderVisible) {
            if (isOnlyUpscaleLoaderVisible && cropContainerRef.current) {
                cropContainerRef.current.style.overflow = '';
                cropContainerRef.current.style.touchAction = '';
            }
            return undefined;
        }

        return setupCropContainerScrollBlock(cropContainerRef.current);
    }, [isCropImageModalVisible, loadedImagesCount, isFullUpscaleLoading, loadingCropType]);

    const footer = (
        <ActionBar
            primaryActions={
                <>
                    <Button mode="secondary" style="accent" stretched onClick={onCloseModal}>
                        {trls(TrlKeys.buttons.cancel)}
                    </Button>
                    <Button
                        mode="primary"
                        style="accent"
                        stretched
                        disabled={!isAllImagesInCropLoaded || !!loadingCropType}
                        data-qa={isAllImagesInCropLoaded ? 'cropping' : ''}
                        onClick={onCropsSave}
                    >
                        {trls(TrlKeys.buttons.save)}
                    </Button>
                </>
            }
        />
    );

    const content = (
        <>
            {isFullUpscaleLoading && loadingCropType ? (
                <UpscaleLoader cropType={loadingCropType} isFullUpscale />
            ) : (
                <div className={styles.cropImageContainer} data-qa="crop-image-container" ref={cropContainerRef}>
                    {BANNER_IMAGE_CROP_TYPES.map((cropType) => (
                        <ImageContent
                            key={cropType}
                            isSomeCropLoading={!!loadingCropType}
                            isQuickCampaign={isQuickCampaign}
                            imageData={images[cropType]}
                            cropType={cropType}
                        />
                    ))}
                </div>
            )}
        </>
    );

    return (
        <>
            <Modal
                visible={isCropImageModalVisible}
                onClose={onCloseModal}
                size="large"
                header={
                    <MagritteModalHeader
                        title={trls(TrlKeys.title)}
                        subtitle={
                            <Text typography="paragraph-1-regular" style="secondary">
                                {trls(TrlKeys.description)}
                            </Text>
                        }
                        actions={<CrossOutlinedSize24 initialColor="secondary" onClick={onCloseModal} />}
                    />
                }
                footer={footer}
                onAfterExit={resetModal}
            >
                {content}
            </Modal>
            <BottomSheet
                height="full-screen"
                visible={isCropImageModalVisible}
                onClose={onCloseModal}
                footer={footer}
                onAfterExit={resetModal}
                header={
                    <NavBar showDivider="always">
                        <NavBar.Pane>
                            <NavBar.Stage>
                                <NavBar.Actions
                                    left
                                    centered
                                    right={<CrossOutlinedSize24 padding={8} onClick={onCloseModal} />}
                                >
                                    <NavBar.TitleContainer
                                        title={
                                            <Text typography="subtitle-1-semibold">
                                                {trls(TrlKeys.titleBottomSheet)}
                                            </Text>
                                        }
                                    />
                                </NavBar.Actions>
                            </NavBar.Stage>
                        </NavBar.Pane>
                    </NavBar>
                }
            >
                {content}
            </BottomSheet>
            <UpscaleCancelConfirmModal
                isUpscaleCancelConfirmModalVisible={isUpscaleCancelConfirmModalVisible}
                hideModal={setIsUpscaleCancelConfirmModalVisibleOff}
                callback={onCloseModal}
            />
        </>
    );
};
